import { Ref } from "react";

import { Text } from "@react-pdf/renderer";

import { ReactToPdfStyles } from "../DDQPDFDocument";
import { ChoiceFormField, DDQForm, FormConfig, MultipleChoiceFormField } from "../types";

import * as Form from "~/components/Form";
import * as Grid from "~/components/Grid";

export type Mode = "edit" | "read" | "print";

export type Props = {
  formConfig: FormConfig;
  register?: () => Ref<any>;
  title: string;
  form: DDQForm;
  mode: Mode;
};

export default function FormSection({ mode, formConfig, register, title, form }: Props) {
  if (mode === "print") {
    return (
      <>
        {title && <Text style={ReactToPdfStyles.sectionTitle}>{title}</Text>}
        {Object.keys(formConfig).map((fieldKey) => getReadOnlyField(fieldKey, formConfig, form, "print"))}
      </>
    );
  }
  return (
    <>
      <Grid.Columns as="section" multiline>
        {title && (
          <Grid.Column mobile={12}>
            <header>
              <Form.Title>{title}</Form.Title>
            </header>
          </Grid.Column>
        )}
        {Object.keys(formConfig).map((fieldKey) => (
          <Grid.Column mobile={12} key={fieldKey}>
            {mode === "read" && getReadOnlyField(fieldKey, formConfig, form, "read")}
            {mode === "edit" && register && getField(fieldKey, formConfig, register)}
          </Grid.Column>
        ))}
      </Grid.Columns>
    </>
  );
}

function getReadOnlyField(fieldKey: string, formConfig: FormConfig, form: DDQForm, mode: "read" | "print") {
  const READONLY_EMPTY_FIELD = "----";
  const value = form.getValues(fieldKey);
  let valueRepresentation = value;
  switch (formConfig[fieldKey].type) {
    case "text":
      valueRepresentation = value || READONLY_EMPTY_FIELD;
      break;
    case "boolean":
      if (value === "true") {
        valueRepresentation = "Yes";
      } else if (value === "false") {
        valueRepresentation = "No";
      } else {
        valueRepresentation = READONLY_EMPTY_FIELD;
      }
      break;
    case "choice":
      if (!value) {
        valueRepresentation = READONLY_EMPTY_FIELD;
      } else {
        const choice = (formConfig[fieldKey] as ChoiceFormField).choices.find((c) => c.value === value);
        valueRepresentation = choice?.label || READONLY_EMPTY_FIELD;
      }
      break;
    case "multipleChoice":
      if (!value) {
        valueRepresentation = READONLY_EMPTY_FIELD;
      } else {
        valueRepresentation = (formConfig[fieldKey] as MultipleChoiceFormField).choices.reduce<any>((prev, acc, i) => {
          if (!value.includes(acc.value)) {
            return prev;
          }
          return [...prev, mode === "read" ? <p key={i}>{acc.label}</p> : <Text key={i}>{acc.label}</Text>];
        }, []);
      }
      break;
    default:
      throw new Error(`Unsupported field type ${formConfig[fieldKey].type}" for field "${fieldKey}"`);
  }

  const Question =
    mode === "read"
      ? ({ children }: { children: string | string[] | React.ReactNode }) => <Form.Label>{children}</Form.Label>
      : ({ children }: { children: string | string[] | React.ReactNode }) => (
          <Text style={ReactToPdfStyles.question}>{children}:</Text>
        );

  const Control =
    mode === "read"
      ? ({ children }: { children: string | string[] }) => <Form.Control className="is-size-7">{children}</Form.Control>
      : ({ children }: { children: string | string[] }) => (
          <>
            {(Array.isArray(children) ? children : [children]).map((c, i) => (
              <Text key={i} style={ReactToPdfStyles.answer}>
                {c}
              </Text>
            ))}
          </>
        );
  return (
    <div key={fieldKey}>
      <Question>{formConfig[fieldKey].label}</Question>
      <Control>{valueRepresentation}</Control>
    </div>
  );
}

function getField(fieldKey: string, formConfig: FormConfig, register: () => Ref<any>) {
  switch (formConfig[fieldKey].type) {
    case "text":
      return (
        <Form.EasyField
          name={fieldKey}
          label={formConfig[fieldKey].label}
          type="text"
          autoComplete="off"
          ref={register()}
        />
      );
    case "boolean":
      return (
        <Form.Field>
          <Form.Fieldset>
            <Form.LabelRadio>{formConfig[fieldKey].label}</Form.LabelRadio>
            <Form.ControlRadio orientation="horizontal">
              <Form.ControlRadioLabel>
                <Form.ControlRadioInput name={fieldKey} value={"false"} ref={register()} />
                <Form.ControlRadioInputText>NO</Form.ControlRadioInputText>
              </Form.ControlRadioLabel>
              <Form.ControlRadioLabel>
                <Form.ControlRadioInput name={fieldKey} value={"true"} ref={register()} />
                <Form.ControlRadioInputText>YES</Form.ControlRadioInputText>
              </Form.ControlRadioLabel>
            </Form.ControlRadio>
          </Form.Fieldset>
        </Form.Field>
      );
    case "choice":
      return (
        <Form.Field>
          <Form.Fieldset>
            <Form.LabelRadio>{formConfig[fieldKey].label}</Form.LabelRadio>
            <Form.ControlRadio>
              {(formConfig[fieldKey] as ChoiceFormField).choices.map((choice) => (
                <Form.ControlRadioLabel key={choice.value} className="mt-2" style={{ marginLeft: 0 }}>
                  <Form.ControlRadioInput name={fieldKey} value={choice.value} ref={register()} />
                  <Form.ControlRadioInputText>{choice.label}</Form.ControlRadioInputText>
                </Form.ControlRadioLabel>
              ))}
            </Form.ControlRadio>
          </Form.Fieldset>
        </Form.Field>
      );
    case "multipleChoice":
      return (
        <Form.Field>
          <Form.Fieldset>
            <Form.LabelRadio>{formConfig[fieldKey].label}</Form.LabelRadio>
            <Form.Control>
              {(formConfig[fieldKey] as MultipleChoiceFormField).choices.map((choice) => (
                <Form.CheckboxLabel key={choice.value} className="mt-2" style={{ marginLeft: 0 }}>
                  <Form.CheckboxInput name={fieldKey} value={choice.value} ref={register()} />
                  <Form.CheckboxText>{choice.label}</Form.CheckboxText>
                </Form.CheckboxLabel>
              ))}
            </Form.Control>
          </Form.Fieldset>
        </Form.Field>
      );
    default:
      throw new Error(`Unsupported field type ${formConfig[fieldKey].type}" for field "${fieldKey}"`);
  }
}
